// File: src/components/Invoices/OperationalFinancePanel.jsx

import React from "react";
import {
  Banknote,
  CreditCard,
  CalendarClock,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
} from "lucide-react";

const currency = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function getDaysBetween(from, to = new Date()) {
  if (!from) return null;

  const fromDate = new Date(from);
  const toDate = new Date(to);

  if (Number.isNaN(fromDate.getTime())) return null;

  const ms = toDate.getTime() - fromDate.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

function getFinanceHealth({ status, remaining, dueDate, paidAt }) {
  const safeStatus = String(status || "").toLowerCase();
  const today = new Date();
  const due = dueDate ? new Date(dueDate) : null;

  if (safeStatus === "rejected") {
    return {
      label: "Blocked",
      icon: AlertTriangle,
      className: "border-red-400/30 bg-red-500/10 text-red-300",
      message: "Invoice is rejected and should not proceed to payment.",
    };
  }

  if (safeStatus === "paid" || paidAt || remaining <= 0) {
    return {
      label: "Healthy",
      icon: CheckCircle,
      className: "border-green-400/30 bg-green-500/10 text-green-300",
      message: "Invoice is fully paid and financially closed.",
    };
  }

  if (due && due < today) {
    return {
      label: "Overdue",
      icon: AlertTriangle,
      className: "border-red-400/30 bg-red-500/10 text-red-300",
      message: "Invoice is past due date and requires finance attention.",
    };
  }

  if (due) {
    const daysUntilDue = Math.ceil(
      (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysUntilDue <= 7) {
      return {
        label: "Due Soon",
        icon: Clock,
        className: "border-amber-400/30 bg-amber-500/10 text-amber-300",
        message: "Invoice is approaching due date.",
      };
    }
  }

  return {
    label: "Open",
    icon: Clock,
    className: "border-blue-400/30 bg-blue-500/10 text-blue-300",
    message: "Invoice is open and progressing through finance workflow.",
  };
}

function buildInsights({ status, amountDue, totalPaid, remaining, dueDate, paidAt }) {
  const insights = [];
  const safeStatus = String(status || "").toLowerCase();

  if (amountDue <= 0) {
    insights.push("Invoice has no payable value recorded yet.");
  }

  if (remaining <= 0 || safeStatus === "paid" || paidAt) {
    insights.push("Invoice is fully paid and can be treated as financially closed.");
  } else {
    insights.push(`${currency(remaining)} remains outstanding.`);
  }

  if (safeStatus === "approved" && remaining > 0) {
    insights.push("Invoice is approved and ready for payment processing.");
  }

  if (safeStatus === "pending approval" || safeStatus === "pending") {
    insights.push("Invoice is waiting for finance approval before payment.");
  }

  if (safeStatus === "rejected") {
    insights.push("Invoice is rejected and should not be included in payment runs.");
  }

  if (totalPaid > 0 && remaining > 0) {
    insights.push("Partial payment has been recorded against this invoice.");
  }

  if (dueDate && remaining > 0) {
    const daysFromDue = getDaysBetween(dueDate);

    if (daysFromDue > 0) {
      insights.push(`Invoice is overdue by ${daysFromDue} day(s).`);
    } else if (daysFromDue <= 0) {
      insights.push("Invoice is not overdue based on the current due date.");
    }
  }

  return insights;
}

export default function OperationalFinancePanel({ invoice }) {
  const amountDue = toNumber(invoice?.amount_due || invoice?.amount || 0);
  const totalPaid = toNumber(invoice?.total_paid || 0);
  const remaining =
    invoice?.balance_due != null
      ? toNumber(invoice.balance_due)
      : invoice?.remaining != null
        ? toNumber(invoice.remaining)
        : Math.max(amountDue - totalPaid, 0);

  const paidPercent =
    amountDue > 0 ? Math.min(Math.max((totalPaid / amountDue) * 100, 0), 100) : 0;

  const status = invoice?.status || invoice?.payment_status || "unknown";
  const dueDate = invoice?.due_date || null;
  const paidAt = invoice?.paid_at || null;
  const uploadedAt = invoice?.uploaded_at || invoice?.created_at || null;

  const health = getFinanceHealth({
    status,
    remaining,
    dueDate,
    paidAt,
  });

  const HealthIcon = health.icon;

  const daysOutstanding =
    paidAt || remaining <= 0 ? 0 : getDaysBetween(uploadedAt) ?? null;

  const insights = buildInsights({
    status,
    amountDue,
    totalPaid,
    remaining,
    dueDate,
    paidAt,
  });

  return (
    <div className="rounded-2xl border border-white/10 bg-[#101010] p-5">
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Finance Control Centre</h2>
          <p className="mt-1 text-sm text-gray-400">
            Payment position, outstanding exposure, due-date risk, and finance workflow status.
          </p>
        </div>

        <span
          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${health.className}`}
        >
          <HealthIcon className="h-4 w-4" />
          {health.label}
        </span>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-white/10 bg-black/25 p-4">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Banknote className="h-4 w-4" />
            Invoice Value
          </div>
          <div className="mt-1 text-xl font-bold">{currency(amountDue)}</div>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/25 p-4">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <CheckCircle className="h-4 w-4" />
            Paid
          </div>
          <div className="mt-1 text-xl font-bold text-green-300">
            {currency(totalPaid)}
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/25 p-4">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <AlertTriangle className="h-4 w-4" />
            Outstanding
          </div>
          <div className="mt-1 text-xl font-bold text-amber-300">
            {currency(remaining)}
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/25 p-4">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <TrendingUp className="h-4 w-4" />
            Paid Progress
          </div>
          <div className="mt-1 text-xl font-bold">{paidPercent.toFixed(0)}%</div>
        </div>
      </div>

      <div className="mt-5 rounded-xl border border-white/10 bg-black/25 p-4">
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="text-gray-300">Payment Progress</span>
          <span className="font-semibold text-white">{paidPercent.toFixed(0)}%</span>
        </div>

        <div className="h-3 overflow-hidden rounded-full bg-white/10">
          <div
            className="h-full rounded-full bg-green-400/70"
            style={{ width: `${paidPercent}%` }}
          />
        </div>
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-white/10 bg-black/25 p-4">
          <div className="text-xs text-gray-400">Payment Status</div>
          <div className="mt-1 font-semibold">{String(status).toUpperCase()}</div>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/25 p-4">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <CalendarClock className="h-4 w-4" />
            Due Date
          </div>
          <div className="mt-1 font-semibold">
            {dueDate ? new Date(dueDate).toLocaleDateString() : "—"}
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/25 p-4">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <CreditCard className="h-4 w-4" />
            Paid Date
          </div>
          <div className="mt-1 font-semibold">
            {paidAt ? new Date(paidAt).toLocaleDateString() : "—"}
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/25 p-4">
          <div className="text-xs text-gray-400">Days Outstanding</div>
          <div className="mt-1 font-semibold">
            {daysOutstanding === null ? "—" : `${daysOutstanding} day(s)`}
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-xl border border-white/10 bg-black/25 p-4">
        <div className="mb-3 font-semibold">Financial Intelligence</div>

        <div className="mb-3 text-sm text-gray-300">{health.message}</div>

        <div className="space-y-2">
          {insights.map((insight, index) => (
            <div key={`${insight}-${index}`} className="flex gap-2 text-sm text-gray-300">
              <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-300" />
              <span>{insight}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}